/**
 * Centralized auth manager: single-flight refresh, no event storms.
 * Only this module and AuthContext may drive session state; authFetch/axios only signal 401
 * and wait for refresh. Only /auth/me (via AuthContext) and refresh failure here determine logout.
 */

import { API_BASE_URL } from '../config';
import { getAuthHeaders, clearSessionToken, SESSION_TOKEN_KEY } from './workosAuth';
import { logAuthEvent } from './authLogger';

let onSessionInvalidated: (() => void) | null = null;

/** Register the single callback for session invalidation (called by AuthContext on mount). */
export function setOnSessionInvalidated(callback: (() => void) | null): void {
  onSessionInvalidated = callback;
}

/** Result of a refresh attempt: success, or failure with status (429 = rate limit, do not invalidate session). */
type RefreshResult = { success: true } | { success: false; status?: number };

const REFRESH_429_RETRY_DELAY_MS = 2500;

async function doRefresh(): Promise<RefreshResult> {
  logAuthEvent({ event: 'refresh_attempt' });
  try {
    const headers = await getAuthHeaders();
    let res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers,
    });
    if (res.status === 429) {
      logAuthEvent({ event: 'refresh_failure', statusCode: 429, reason: 'rate_limited_retry' });
      await new Promise((r) => setTimeout(r, REFRESH_429_RETRY_DELAY_MS));
      res = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers,
      });
    }
    if (!res.ok) {
      logAuthEvent({ event: 'refresh_failure', statusCode: res.status, reason: 'refresh_endpoint_failed' });
      return { success: false, status: res.status };
    }
    const data = await res.json().catch(() => ({}));
    const accessToken = data?.access_token;
    if (accessToken && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(SESSION_TOKEN_KEY, accessToken);
    }
    logAuthEvent({ event: 'refresh_success' });
    return { success: true };
  } catch (err) {
    logAuthEvent({ event: 'refresh_failure', reason: 'network_error' });
    return { success: false };
  }
}

let refreshPromise: Promise<RefreshResult> | null = null;

/**
 * Handle a 401 from any request. Single-flight: only one refresh runs; others wait on the same promise.
 * Returns true if the session was recovered (caller should retry the request); false if session is dead or rate limited.
 * Only invokes onSessionInvalidated when refresh failed with 401 (session truly invalid). 429 = rate limit, do not logout.
 */
export async function handle401(): Promise<boolean> {
  logAuthEvent({ event: 'auth_401_received' });
  if (refreshPromise === null) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  const result = await refreshPromise;
  if (result.success === false) {
    if (result.status === 429) {
      return false;
    }
    logAuthEvent({ event: 'session_invalidated', reason: 'refresh_failed' });
    clearSessionToken();
    onSessionInvalidated?.();
    return false;
  }
  return true;
}

/**
 * Request a token refresh (e.g. proactive before expiry). Single-flight with handle401.
 * Returns true if refresh succeeded, false otherwise. Only calls onSessionInvalidated when refresh returned 401 (not 429).
 */
export async function requestRefresh(): Promise<boolean> {
  if (refreshPromise === null) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  const result = await refreshPromise;
  if (result.success === false) {
    if (result.status === 429) return false;
    logAuthEvent({ event: 'session_invalidated', reason: 'refresh_failed' });
    clearSessionToken();
    onSessionInvalidated?.();
    return false;
  }
  return true;
}

/**
 * Run a request that may get 401. If 401, run single-flight refresh and retry once.
 * Returns response or throws. Does not clear tokens or redirect; on refresh failure calls onSessionInvalidated.
 */
export async function withAuthRetry<T>(
  request: () => Promise<T>
): Promise<T> {
  let result: T;
  try {
    result = await request();
  } catch (err: unknown) {
    const res = err && typeof err === 'object' && 'response' in err
      ? (err as { response?: { status?: number } }).response
      : undefined;
    if (res?.status !== 401) throw err;
    const recovered = await handle401();
    if (!recovered) throw err;
    result = await request();
  }
  return result;
}
